import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { STORE_HERO_BGS, contentFor, type StoreItem, type StoreWithItems } from "@nh/shared";

/**
 * ═══════════════════════════════════════════════════════════════════
 * La PORTADA de la tienda personalizada — el gesto del héroe de la
 * casa, en versión de la franquiciataria (Anahí, 27-ago): tinta
 * profunda del color que ELLA eligió (`heroBg`), el titular a la
 * izquierda, y a la derecha —donde la casa pone su vídeo— SUS
 * productos flotando: los que marcó en `heroProductIds` (o los
 * primeros de su vitrina si no marcó ninguno).
 *
 * La flotación es framer-motion con duraciones distintas por botella
 * para que no naden sincronizadas; con `prefers-reduced-motion` se
 * quedan quietas — se apaga el movimiento, no la portada.
 * ═══════════════════════════════════════════════════════════════════
 */
export function StoreHero({
  store,
  items,
  accent,
}: {
  store: StoreWithItems["store"];
  items: StoreItem[];
  accent: string;
}) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const floating = useMemo(() => {
    const chosen = store.branding.heroProductIds
      .map((id) => items.find((item) => item.product.id === id))
      .filter((item): item is StoreItem => Boolean(item?.product.imageUrl));
    const pool = chosen.length > 0
      ? chosen
      : items.filter((item) => item.product.imageUrl).slice(0, 3);
    return pool.slice(0, 3);
  }, [store.branding.heroProductIds, items]);

  /* Las posiciones de las tres botellas en la mitad derecha. */
  const SPOTS = [
    { right: "22%", top: "14%", size: 190, duration: 6.5, delay: 0 },
    { right: "4%", top: "38%", size: 150, duration: 7.5, delay: 0.8 },
    { right: "34%", top: "52%", size: 130, duration: 8.5, delay: 1.6 },
  ] as const;

  return (
    <section
      className="relative isolate overflow-hidden"
      style={{ background: STORE_HERO_BGS[store.branding.heroBg] }}
    >
      {/* El resplandor del acento, respirando detrás de todo. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-1/2 h-[30rem] w-[30rem] -translate-y-1/2 rounded-full blur-[140px]"
        style={{ background: `color-mix(in srgb, ${accent} 35%, transparent)` }}
      />

      {/* ── Los productos flotando, a la derecha ─────────────────────── */}
      <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 sm:block">
        {floating.map((item, index) => {
          const spot = SPOTS[index]!;
          const content = contentFor(item.product.translations, "es");
          return (
            <motion.img
              key={item.product.id}
              src={item.product.imageUrl ?? ""}
              alt=""
              className="absolute rounded-3xl object-contain drop-shadow-[0_24px_40px_rgba(0,0,0,0.45)]"
              style={{ right: spot.right, top: spot.top, width: spot.size }}
              title={content.name}
              initial={false}
              animate={
                reduceMotion
                  ? {}
                  : { y: [0, -14, 0], rotate: [0, index % 2 === 0 ? 2.5 : -2.5, 0] }
              }
              transition={{
                duration: spot.duration,
                delay: spot.delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </div>

      {/* ── El titular ───────────────────────────────────────────────── */}
      <div className="relative mx-auto w-full max-w-[1200px] px-4 py-16 sm:px-8 sm:py-24">
        <div className="max-w-[560px]">
          <p
            className="text-[11px] font-bold uppercase tracking-ds-label"
            style={{ color: `color-mix(in srgb, ${accent} 60%, white)` }}
          >
            {t("Tienda de {{name}} · It's The Best", { name: store.ownerName })}
          </p>
          <h1 className="mt-3 text-[38px] font-light leading-[1.05] tracking-tight text-white sm:text-[52px]">
            {t("Bienvenido a")}{" "}
            <span className="font-semibold" style={{ color: `color-mix(in srgb, ${accent} 55%, white)` }}>
              {store.name}
            </span>
          </h1>
          {store.branding.welcome && (
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-white/70">
              {store.branding.welcome}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
